import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { QuoteDetail } from "@/components/admin/QuoteDetail";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const EVENT_STATUSES = ["approvato", "acconto_ricevuto", "confermato", "completato"];
const CHIP = {
  approvato: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  acconto_ricevuto: "bg-teal-500/15 text-teal-300 border-teal-500/40",
  confermato: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  completato: "bg-slate-500/15 text-slate-400 border-slate-600",
};
const LEGEND = [["approvato", "Approvato"], ["acconto_ricevuto", "Acconto ricevuto"], ["confermato", "Confermato"], ["completato", "Completato"]];
const pad = (n) => String(n).padStart(2, "0");

export const EventsCalendar = () => {
  const [month, setMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [quotes, setQuotes] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const [q, b, res] = await Promise.all([
      api.get("/admin/quotes", { params: { status: "tutti" } }),
      api.get("/admin/blocked-dates"),
      api.get("/admin/reservations"),
    ]);
    setQuotes(q.data.filter((x) => EVENT_STATUSES.includes(x.status)));
    setBlocked(b.data.manual.map((x) => x.date));
    setReservations(res.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const first = new Date(month.y, month.m, 1);
  const offset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(month.y, month.m + 1, 0).getDate();
  const t = new Date();
  const todayStr = `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
  const byDate = {};
  quotes.forEach((q) => { (byDate[q.event.date] = byDate[q.event.date] || []).push(q); });
  const nav = (dir) => setMonth(({ y, m }) => { const d = new Date(y, m + dir, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  return (
    <div className="bg-[#1E293B] border border-slate-700 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#C85A32]">Calendario eventi</h3>
        <div className="flex items-center gap-1">
          <button data-testid="calendar-prev-month" onClick={() => nav(-1)} className="p-1.5 text-slate-400 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
          <span data-testid="calendar-month-label" className="text-sm font-semibold w-36 text-center">{MONTHS[month.m]} {month.y}</span>
          <button data-testid="calendar-next-month" onClick={() => nav(1)} className="p-1.5 text-slate-400 hover:text-white"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        {DAYS.map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`x${i}`} />;
          const ds = `${month.y}-${pad(month.m + 1)}-${pad(day)}`;
          const evs = byDate[ds] || [];
          const res = reservations.filter((r) => r.date === ds);
          const isBlocked = blocked.includes(ds);
          return (
            <div key={ds} data-testid={`calendar-day-${ds}`}
              className={`min-h-[64px] sm:min-h-[84px] rounded-lg border p-1 ${ds === todayStr ? "border-[#C85A32] bg-[#C85A32]/10" : "border-slate-700/60 bg-[#0F172A]"}`}>
              <div className="flex items-center justify-between px-0.5">
                <span className={`text-[11px] font-mono-num ${ds === todayStr ? "text-[#C85A32] font-bold" : "text-slate-400"}`}>{day}</span>
                {isBlocked && <Lock className="w-3 h-3 text-slate-500" title="Data bloccata" />}
              </div>
              <div className="space-y-0.5 mt-0.5">
                {evs.map((q) => (
                  <button key={q.id} data-testid={`calendar-event-${q.id}`} onClick={() => setSelected(q.id)}
                    className={`w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded border truncate hover:brightness-125 transition ${CHIP[q.status]}`}
                    title={`#${q.number} ${q.contact.name} — ${q.event.type} ${q.event.time_start}`}>
                    <span className="font-mono-num">{q.event.time_start}</span> {q.contact.name}
                  </button>
                ))}
                {res.length > 0 && (
                  <div data-testid={`calendar-res-${ds}`} title={res.map((r) => `${r.time || ""} ${r.name} x${r.people}`).join("\n")}
                    className="text-[10px] leading-tight px-1 py-0.5 rounded border truncate bg-sky-500/15 text-sky-300 border-sky-500/40">
                    {res.length} tavol{res.length === 1 ? "o" : "i"} · {res.reduce((s, r) => s + r.people, 0)} pers.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        {LEGEND.map(([s, label]) => (
          <span key={s} className="flex items-center gap-1.5 text-[11px] text-slate-400">
            <span className={`w-3 h-3 rounded border ${CHIP[s]}`} /> {label}
          </span>
        ))}
        <span className="flex items-center gap-1.5 text-[11px] text-slate-400"><span className="w-3 h-3 rounded border bg-sky-500/15 border-sky-500/40" /> Tavoli prenotati</span>
        <span className="flex items-center gap-1.5 text-[11px] text-slate-400"><Lock className="w-3 h-3" /> Data bloccata</span>
      </div>
      {selected && <QuoteDetail quoteId={selected} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
};
